/*
* Reserve study chart - report building logic
*/

import * as _ from 'lodash';

import { isNullOrUndefined } from '../../src/utils/generalUtils';
import * as AppUtilities from '../common/apputilities';
import { isNumeric } from '../common/apputilities';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import { CalculateYears } from './report-common';

export const usefulLifeOneTime = '1TE';
export const usefulLifeNA = 'N/A';
export const getFFBFloatDecimals = 5;

/* Item expenditure map item */
export class ItemExpenditureData {
    public componentId: number = 0;
    public inventoryId: number = 0;
    public expense: number = 0;
    public hypoExpense: number = 0;
    public oneTimeExpense: boolean = false;
    public hypoOneTimeExpense: boolean = false;
    public projectedExpense?: number = 0;
    public hypoProjectedExpense?: number = 0;
    public projectedExpenseShare: number = 0;
    public hypoProjectedExpenseShare: number = 0;
    public projectedShareFactor: number = 0;
    public hypoProjectedShareFactor: number = 0;
    public remainingLife: number = 0;
    public hypoRemainingLife: number = 0;
    public usefulLife: number = 0;
    public hypoUsefulLife: number = 0;
    public signAsDollar: number = 0;
    public year: number = 0;

    public constructor(year: number, inventoryId?: number, componentId?: number) {
        this.year = year;

        if (inventoryId) { this.inventoryId = inventoryId; }
        if (componentId) { this.componentId = componentId; }
    }
}

/* Reserve study report calculation logic */
export const getFFBByYear = (invMap: Array<any>, yearIndex: number) => {
    let result = 0;

    invMap.forEach((item: any) => {
        const peShare = Formatters.getFloat(item, `expenditureMap[${yearIndex}].projectedExpenseShare`, getFFBFloatDecimals);
        const peTotal = Formatters.getFloat(item, `expenditureMap[${yearIndex}].projectedExpense`, getFFBFloatDecimals);
        const peFactor = Formatters.getFloat(item, `expenditureMap[${yearIndex}].projectedShareFactor`, getFFBFloatDecimals);

        // console.log(`getFFBByYear yearIndex ${yearIndex} compId ${_.get(item, 'componentId')} peTotal ${peTotal} peFactor ${peFactor} peShare ${peShare}`, {item});

        result += peShare;
    });

    return result;
};

export const getEFFBByYear = (invMap: Array<any>, yearIndex: number) => {
    let result = 0;
    let nextYearIndex = yearIndex + 1;

    invMap.forEach((item: any) => {
        result += Formatters.getFloat(item, `expenditureMap[${nextYearIndex}].projectedExpenseShare`, getFFBFloatDecimals);
    });
    if (result <= 0) {
        result = 0.0;
    }
    return result;
};

export const getHypoFFBByYear = (invMap: Array<any>, yearIndex: number) => {
    let result = 0;

    invMap.forEach((item: any) => {
        result += Formatters.getFloat(item, `expenditureMap[${yearIndex}].hypoProjectedExpenseShare`, getFFBFloatDecimals);
    });

    return result;
};

export const getHypoEFFBByYear = (invMap: Array<any>, yearIndex: number) => {
    let result = 0;
    let nextYearIndex = yearIndex + 1;

    invMap.forEach((item: any) => {
        result += Formatters.getFloat(item, `expenditureMap[${nextYearIndex}].hypoProjectedExpenseShare`, getFFBFloatDecimals);
    });
    if (result <= 0) {
        result = 0.0;
    }
    return result;
};

export const getExpendituresByYear = (invMap: Array<any>, yearIndex: number) => {
    let result = 0;

    invMap.forEach((item: any) => {
        // 926 working
        // result += Formatters.getFloat(item, `expenditureMap[${yearIndex}].expense`, 2, 1, 0);
        result += Formatters.getFloat(item, `expenditureMap[${yearIndex}].expense`, getFFBFloatDecimals, 1, 0);
    });

    return result;
};

export const getHypoExpendituresByYear = (invMap: Array<any>, yearIndex: number) => {
    let result = 0;

    invMap.forEach((item: any) => {
        // 926 working
        // result += Formatters.getFloat(item, `expenditureMap[${yearIndex}].hypoExpense`, 2, 1, 0);
        result += Formatters.getFloat(item, `expenditureMap[${yearIndex}].hypoExpense`, getFFBFloatDecimals, 1, 0);
    });

    return result;
};

export const getPercentFunded = (records: Array<any>, isHypothetical: boolean, capAt100Percent: boolean = true) => {
    let result = 0;

    if (!AppUtilities.isArrayWithLength(records)) { return result; }

    const firstRecord = records[0];
    result = (isHypothetical) ? _.get(firstRecord, 'hypoPctFunded', 0) : _.get(firstRecord, 'currentPctFunded', 0);

    // if (result > 100) { result = 100; }
    if (result < 0) { result = 0; }

    if (result > 0) {
        if (capAt100Percent) {
            result = Math.round(result * 100) / 100;
        } else {
            result = (result / 100) * 100;
        }
    }

    return result;
};

export const getFundedAmount = (records: Array<any>, isHypothetical: boolean, capAt100Percent: boolean = true) => {
    let result = 0;

    if (!AppUtilities.isArrayWithLength(records)) { return result; }

    const firstRecord = records[0];
    result = (isHypothetical) ? _.get(firstRecord, 'hypoBalance', 0) : _.get(firstRecord, 'currentStartingBalance', 0);
    return result;
};

export const getFullyFunded = (records: Array<any>, isHypothetical: boolean, capAt100Percent: boolean = true) => {
    let result = 0;

    if (!AppUtilities.isArrayWithLength(records)) { return result; }

    const firstRecord = records[0];
    result = (isHypothetical) ? _.get(firstRecord, 'hypoFFB', 0) : _.get(firstRecord, 'currentFFB', 0);
    return result;
};

export const futureValue = (rate: number, nPer: number, value: number, presentValue: number, type: number = 0) => {
    const result = rate === 0 ? -1 * (presentValue + value * nPer) : (value * (1 + rate * type) * (1 - Math.pow((1 + rate), nPer)) / rate) - presentValue * Math.pow((1 + rate), nPer);

    return result;
};

export const truncateNumber = (num: number, digits: number = 2) => {
    const numS = num.toString();
    const decPos = numS.indexOf('.');
    const substrLength = (decPos === -1) ? numS.length : 1 + decPos + digits;

    const trimmedResult = numS.substr(0, substrLength);
    const res = parseFloat(trimmedResult);

    // console.log(`truncateNumber num ${num} digits ${digits} res: ${res}`)

    return res;
};

export const forceSQLLikePercision = (val: number, digits: number = 16) => {
    const res = _.round(val, digits); // Math.round((val) * 1000000) * .000001;

    return res;
};

export const forceSQLLikePercisionINACCURATE = (val: number) => {
    const res = Math.round((val) * 1000000) * .000001;

    return res;
};

export const buildMRCTable = (mrc: number, interest: number, secInterest: number, secYear: number, length: number = 30, name: string = '') => {
    let tb: Array<any> = [];
    let yearNum = 0;

    let mrcCopy = Number(mrc);

    if (isNumeric(secInterest) && isNumeric(secYear)) {
        tb.push(Number(mrcCopy));

        while (yearNum < secYear) {
            mrcCopy = mrcCopy * (1 + interest);
            tb.push(Number(mrcCopy));
            yearNum++;
        }

        while (yearNum < length) {
            mrcCopy = mrcCopy * (1 + secInterest);
            tb.push(Number(mrcCopy));
            yearNum++;
        }
    } else {
        tb.push(Number(mrcCopy));

        yearNum++;
        while (yearNum < length) {
            mrcCopy = mrcCopy * (1 + interest);

            tb.push(Number(mrcCopy));
            yearNum++;
        }
    }

    return tb;
};

export const buildInvMap = (inventory: Array<any>, calculateYears: number, beginYear: number, inflation: number, hypoInflation: number) => {
    return inventory.map((c, i) => {
        let item = _.cloneDeep(c);

        if (isNullOrUndefined(hypoInflation)) { hypoInflation = inflation; }
        if (isNullOrUndefined(item.avgCost)) { item.avgCost = Number(calculateInventoryItemAvgCost(item)); }
        if (isNullOrUndefined(item.hypAvgCost)) { item.hypAvgCost = item.avgCost; }
        if (isNullOrUndefined(item.hypRemainingUsefulLife)) { item.hypRemainingUsefulLife = (AppUtilities.isNumeric(item.remainingUsefulLife)) ? Number(item.remainingUsefulLife) : 0; }
        if (isNullOrUndefined(item.hypUsefulLife)) { item.hypUsefulLife = isNaN(item.usefulLife) ? item.usefulLife : Number(item.usefulLife); }
        item.hypHidden = !_.get(item, 'included', (item.hypAvgCost === 0) ? false : true);
        if (item.usefulLife === 'N/A') { item.avgCost = 0.0; }

        let ul: number = 30;
        let ote: boolean = false;
        let rul: number = Formatters.getInt(item, 'remainingUsefulLife');

        if (isNaN(item.usefulLife) || item.usefulLife === 9999) {
            if (item.usefulLife.toUpperCase() === usefulLifeOneTime || item.usefulLife === 9999) {
                ote = true;
                ul = (item.remainingUsefulLife) ? item.remainingUsefulLife + 1 : 1;
            }
        } else {
            ul = parseInt(item.usefulLife, 10);
        }

        let hul: number = 30;
        let hote: boolean = false;
        let hrul: number = Formatters.getInt(item, 'hypRemainingUsefulLife');

        if (isNaN(item.usefulLife) || isNaN(item.hypUsefulLife)) {
            if ((_.upperCase(item.usefulLife) === _.upperCase(usefulLifeOneTime)) || (_.upperCase(item.hypUsefulLife) === _.upperCase(usefulLifeOneTime))) {
                hote = true;
                // hul = 30;
                item.hypUsefulLife = 30;
            } else if (_.upperCase(item.usefulLife) === _.upperCase(usefulLifeNA)) {
                if (_.upperCase(item.hypUsefulLife) === _.upperCase(usefulLifeNA)) {
                    hote = true;
                    item.hypHidden = true;
                } else {
                    hul = parseInt(item.hypUsefulLife, 10);
                }
            }
        } else {
            hul = parseInt(item.hypUsefulLife, 10);
        }

        // Calculate yearly item expenses
        let itemExpenditureDataMap: Array<ItemExpenditureData> = [];
        let oteAccountedFor: boolean = false;
        for (let yearNum: number = 0; yearNum < calculateYears; yearNum++) {
            const curYear: number = beginYear + yearNum;
            if (oteAccountedFor) {
                // Insert empty entries into expenditureMap
                const singleItemExpenditure: ItemExpenditureData = new ItemExpenditureData(curYear, Formatters.getInt(item, 'inventoryId'), item.componentId);

                // Projected Expense
                singleItemExpenditure.projectedExpense = 0;
                singleItemExpenditure.projectedExpenseShare = 0;
                singleItemExpenditure.projectedShareFactor = 0;

                singleItemExpenditure.expense = 0;

                singleItemExpenditure.remainingLife = Number(rul);
                singleItemExpenditure.usefulLife = Number(ul);
                singleItemExpenditure.oneTimeExpense = ote ? true : false;
                singleItemExpenditure.signAsDollar = (Formatters.getFloat(item, 'avgCost') > 0 && ul > 0) ? item.avgCost / Number(ul) : 0;
                itemExpenditureDataMap.push(_.cloneDeep(singleItemExpenditure));
                continue;
            }

            const expenseSqlDigits = 20;

            // CALCULATE PROJECTED EXPENSE (projectedExpenseShare USED AS "curBal" VALUE IN REPORT)
            const projectedExpense: number = Number(futureValue(inflation, yearNum, 0, (Formatters.getFloat(item, 'avgCost') * -1)));
            const valPreProjectedShareFactor: number = Number((ul - rul) / ul);
            const projectedShareFactor: number = Number(forceSQLLikePercision(valPreProjectedShareFactor, expenseSqlDigits));

            let projectedExpenseShare: number = 0;
            if (ul >= rul) {
                projectedExpenseShare = Number(projectedExpense * projectedShareFactor);
            }

            // let msg = `logic::buildInvMap PE yearNum ${yearNum} curYear ${curYear} componentId ${item.componentId} name ${item.componentName}`;
            // msg += ` projectedExpense ${projectedExpense}`;
            // msg += ` projectedExpenseShare ${projectedExpenseShare}`;
            // msg += ` inflation ${inflation} getFloat(item.avgCost) ${Formatters.getFloat(item, 'avgCost')}`;
            // msg += ` valPreProjectedShareFactor ${valPreProjectedShareFactor} projectedShareFactor ${projectedShareFactor}`;
            // console.log(msg, { item });

            // CALCULATE HYPO PROJECTED EXPENSE (hypoProjectedExpenseShare USED AS "idealBal" VALUE IN REPORT)
            const hypoProjectedExpense: number = Number(item.hypHidden ? 0 : futureValue(hypoInflation, yearNum, 0, (Formatters.getFloat(item, 'hypAvgCost') * -1)));
            const valPreHypoProjectedShareFactor = ((hul - hrul) / hul);
            const hypoProjectedShareFactor: number = Number(forceSQLLikePercision(valPreHypoProjectedShareFactor, expenseSqlDigits));
            // console.log(`hypPe item hypoInflation ${hypoInflation} avgcost ${item.hypAvgCost} getFloat ${Formatters.getFloat(item, 'hypAvgCost')} valPre ${valPreHypoProjectedShareFactor} hypoProjectedShareFactor ${hypoProjectedShareFactor}`);
            let hypoProjectedExpenseShare: number = 0;
            if (hote) {
                hypoProjectedExpenseShare = ((hypoProjectedExpense / (hrul + 1)));
            } else {
                if (hul >= hrul) {
                    hypoProjectedExpenseShare = Number(hypoProjectedExpense * hypoProjectedShareFactor);
                }
            }

            const itemExpenditureData: ItemExpenditureData = new ItemExpenditureData(curYear, Formatters.getInt(item, 'inventoryId'), item.componentId);

            // Projected Expense
            itemExpenditureData.projectedExpense = Number(projectedExpense);
            itemExpenditureData.projectedExpenseShare = Number(projectedExpenseShare);
            itemExpenditureData.projectedShareFactor = Number(projectedShareFactor);

            itemExpenditureData.expense = (_.isInteger(rul) && rul === 0) ? Number(projectedExpense) : 0;

            itemExpenditureData.remainingLife = Number(rul);
            itemExpenditureData.usefulLife = Number(ul);
            itemExpenditureData.oneTimeExpense = ote ? true : false;
            itemExpenditureData.signAsDollar = (Formatters.getFloat(item, 'avgCost') > 0 && ul > 0 && ul !== 9999 && typeof (ul) !== 'string') ? item.avgCost / Number(ul) : 0;
            if (ote && rul === 0) {
                itemExpenditureData.signAsDollar = 0;
            }

            // Projected Hypothetical Expense
            itemExpenditureData.hypoProjectedExpense = Number(hypoProjectedExpense);
            itemExpenditureData.hypoProjectedExpenseShare = Number(hypoProjectedExpenseShare);
            itemExpenditureData.hypoProjectedShareFactor = Number(hypoProjectedShareFactor);

            itemExpenditureData.hypoExpense = _.isInteger(hrul) && hrul === 0 ? Number(hypoProjectedExpense) : 0;

            itemExpenditureData.hypoRemainingLife = Number(hrul);
            itemExpenditureData.hypoUsefulLife = Number(hul);
            itemExpenditureData.hypoOneTimeExpense = hote ? true : false;

            if (yearNum >= 0) {
                const oldRul = rul;
                if (rul > 0) {
                    rul--;
                } else {
                    if (ote) {
                        oteAccountedFor = true;
                    } else {
                        rul = ul - 1;
                    }
                }

                const oldHrul = hrul;
                if (hrul > 0) {
                    hrul--;
                } else {
                    hrul = hul - 1;
                }

                // console.log(`year ${yearNum} item ${_.get(item, 'componentId')} rul changed from ${oldRul} to ${rul} and hrul from ${oldHrul} to ${hrul}`);
            }

            itemExpenditureDataMap.push(_.cloneDeep(itemExpenditureData));
        }

        item.expenditureMap = _.cloneDeep(itemExpenditureDataMap);
        if (_.isFinite(_.parseInt(item.subgroup)) && _.inRange(_.parseInt(item.subgroup), 1, 10)) {
            item = FormUtilities.padSubgroup(item);
        }

        return item;
    });
};

export const buildSpecialAssessments = (currentStartingYear: number, nonStandardSpecialAssessments: any) => {
    let saGrid: Array<any> = [];
    for (let i = 0; i < 30; i++) {
        let calendarYearPerIteration = currentStartingYear + i;
        let calendarYearPerIterationAsString = _.toString(calendarYearPerIteration);
        let saGridObject = _.find(nonStandardSpecialAssessments, ['year', calendarYearPerIterationAsString]);
        let nonStandardSpecialAssessmentAmount = Number(_.get(saGridObject, 'amount', 0));

        saGrid.push({ 'year': calendarYearPerIteration, 'amount': nonStandardSpecialAssessmentAmount, 'clientRequested': false });
    }
    return saGrid;
};

export const computeMrcUdfMRC = (mrc: number, interest: number, year: number) => {
    let result: number = mrc;
    let yearNum: number = 0;

    while (yearNum < year) {
        result = result * (1 + interest);
        yearNum++;
    }

    return result;
};

export const computeMrcUdfMrcSecondary = (mrc: number, interest: number, secondaryInterest: number, year: number, yearSecondaryRate: number) => {
    let result: number = mrc;
    let yearNum: number = 0;

    while (yearNum < yearSecondaryRate) {
        result = result * (1 + interest);
        yearNum++;
    }

    while (yearNum < year) {
        result = result * (1 + secondaryInterest);
        yearNum++;
    }

    return result;
};

export const computeAnnualInterest = (isFirstYear: boolean, currentInterestVal: number, currentPerFirstYear: number, currentStdMonths) => {
    const roundDecimals = 20;
    const fyPowInner = (currentInterestVal / currentPerFirstYear + 1);
    const fyPowInnerRounded = _.round(fyPowInner, roundDecimals);
    const fyPowRes = Math.pow(fyPowInnerRounded, currentPerFirstYear) - 1;

    const lyPowInner = (currentInterestVal / currentStdMonths + 1);
    const lyPowInnerRounded = _.round(lyPowInner, roundDecimals);
    const lyPowRes = Math.pow(lyPowInnerRounded, currentStdMonths) - 1;

    const resPresIn = isFirstYear ? fyPowRes : lyPowRes;
    const resPres = forceSQLLikePercision(resPresIn, 20);

    const res = truncateNumber(resPres, 20); // 17); // was working with 17

    return res;
};

export const computeItemPercentFunded = (startingBalance: number, currentFfb: number) => {
    if (!startingBalance || !currentFfb) { return 0; }

    let res = (startingBalance / currentFfb) * 100;
    if (res < 0) { res = 0; }

    return res;
};

export const computeFundingCalculations = (currentOrderDetails: any, currentOrder: any, inventory: Array<any>, yearsCalculate: number = -1) => {
    let recordSet: Array<any> = [];
    let invMap: Array<any> = [];

    if (!currentOrderDetails || !currentOrder || !AppUtilities.isArrayWithLength(inventory)) { return {}; }

    let years = CalculateYears; // yearsCalculate;
    const mrcTableYears = CalculateYears;

    let CurrentMRCVal = Number(Formatters.getFloat(currentOrderDetails, 'currentMRCVal', getFFBFloatDecimals, 1, Formatters.getFloat(currentOrder, 'currentMRC', getFFBFloatDecimals, 1)));
    let MinimumMRCVal = Number(Formatters.getFloat(currentOrderDetails, 'minimumMRCVal', getFFBFloatDecimals, 1, Formatters.getFloat(currentOrder, 'minimumMRC', getFFBFloatDecimals, 1)));
    let RecommendedMRCVal = Number(Formatters.getFloat(currentOrderDetails, 'recommendedMRCVal', getFFBFloatDecimals, 1, Formatters.getFloat(currentOrder, 'recommendedMRC', getFFBFloatDecimals, 1)));
    let hypoMRCVal = Number(Formatters.getFloat(currentOrderDetails, 'hypoMRCVal', getFFBFloatDecimals, 1, RecommendedMRCVal));
    // adding support for calculating the values used on the CA-Disclosure page, roughly based on 'current' values but may be entered differently on that page
    let caDiscMRCVal = Number(Formatters.getFloat(currentOrderDetails, 'caDiscMRCVal', getFFBFloatDecimals, 1, Formatters.getFloat(currentOrder, 'currentMRC', getFFBFloatDecimals, 1)));

    const caDiscSpecialAssessments = Number(Formatters.getFloat(currentOrderDetails, 'caDiscApprovedSpecialAssessment', getFFBFloatDecimals, 1));
    const beginningBalance = Number(Formatters.getFloat(currentOrderDetails, 'begBalance', getFFBFloatDecimals, 1));

    const currentInterest = Number(Formatters.getFloat(currentOrderDetails, 'interest', getFFBFloatDecimals));

    const currentInflation = Number(Formatters.getFloat(currentOrderDetails, 'inflation', getFFBFloatDecimals));
    const currentPerFirstYear = Number(Formatters.getFloat(currentOrderDetails, 'perFirstYear', getFFBFloatDecimals, 1));
    const currentStdMonths = Number(Formatters.getInt(currentOrderDetails, 'stdMonths', 12));
    const currentBeginYear = Number(Formatters.getInt(currentOrderDetails, 'beginYear', new Date().getFullYear()));

    const hypoInterest = Number(Formatters.getFloat(currentOrderDetails, 'hypoInterest', getFFBFloatDecimals, 1, 0));
    const hypoSA = Number(Formatters.getFloat(currentOrderDetails, 'hypoSA', getFFBFloatDecimals, 1, 0));
    const hypoInflation = Number(Formatters.getFloat(currentOrderDetails, 'hypoInflation', getFFBFloatDecimals, 1, 0));

    let hypoBegBalance = Number(Formatters.getFloat(currentOrderDetails, 'hypoBegBalance', getFFBFloatDecimals, 1, beginningBalance));
    let hypoRunningBalance = 0;
    let hypoEndingBalance = 0;

    const cAnnualIncrease = Number(Formatters.getFloat(currentOrderDetails, 'currentAnnualIncrease', getFFBFloatDecimals, 1));
    const cSecondaryAnnualIncrease = Number(Formatters.getFloat(currentOrderDetails, 'currentSecondaryAnnualIncrease', getFFBFloatDecimals, 1));
    const cSecondIncreaseYears = Number(Formatters.getInt(currentOrderDetails, 'currentYears', 30));

    const rAnnualIncrease = Number(Formatters.getFloat(currentOrderDetails, 'recommendedAnnualIncrease', getFFBFloatDecimals, 1));
    const rSecondaryAnnualIncrease = Number(Formatters.getFloat(currentOrderDetails, 'recommendedSecondaryAnnualIncrease', getFFBFloatDecimals, 1));
    const rSecondIncreaseYears = Number(Formatters.getInt(currentOrderDetails, 'recommendedYears', CalculateYears));

    const mAnnualIncrease = Number(Formatters.getFloat(currentOrderDetails, 'minimumAnnualIncrease', getFFBFloatDecimals));
    const mSecondaryAnnualIncrease = Number(Formatters.getFloat(currentOrderDetails, 'minimumSecondaryAnnualIncrease', getFFBFloatDecimals));
    const mSecondIncreaseYears = Number(Formatters.getInt(currentOrderDetails, 'minimumYears', CalculateYears));

    const hAnnualIncrease = (Number(Formatters.getFloat(currentOrderDetails, 'hypoAnnualIncrease', getFFBFloatDecimals))) ? (Number(Formatters.getFloat(currentOrderDetails, 'hypoAnnualIncrease', getFFBFloatDecimals))) : 0;
    const hSecondaryAnnualIncrease = (Number(Formatters.getFloat(currentOrderDetails, 'hypoSecondaryAnnualIncrease', getFFBFloatDecimals))) ? (Number(Formatters.getFloat(currentOrderDetails, 'hypoSecondaryAnnualIncrease', getFFBFloatDecimals))) : 0;
    const hSecondIncreaseYears = (Number(Formatters.getInt(currentOrderDetails, 'hypoYears'))) ? (Number(Formatters.getInt(currentOrderDetails, 'hypoYears'))) : 0;

    const caDiscAnnualIncrease = (Number(Formatters.getFloat(currentOrderDetails, 'caDiscAnnualIncrease', getFFBFloatDecimals))) ? (Number(Formatters.getFloat(currentOrderDetails, 'caDiscAnnualIncrease', getFFBFloatDecimals))) : 0;
    const caDiscSecondaryAnnualIncrease = (Number(Formatters.getFloat(currentOrderDetails, 'caDiscSecondaryAnnualIncrease', getFFBFloatDecimals))) ?
        (Number(Formatters.getFloat(currentOrderDetails, 'caDiscSecondaryAnnualIncrease', getFFBFloatDecimals))) : 0;
    const caDiscSecondIncreaseYears = (Number(Formatters.getInt(currentOrderDetails, 'caDiscYears'))) ? (Number(Formatters.getInt(currentOrderDetails, 'caDiscYears'))) : 0;

    const nonStandardSpecialAssessments = _.get(currentOrderDetails, 'specialAssessments[0]', []);
    const nonStandardSpecialAssessmentsCustomer = _.get(currentOrderDetails, 'specialAssessmentsCustomer[0]', []);
    const currentSA = (_.size(nonStandardSpecialAssessmentsCustomer) > 0) ?
        ((Number(Formatters.getStr(nonStandardSpecialAssessmentsCustomer[0], 'year')) === currentBeginYear) ?
            Number(Formatters.getStr(nonStandardSpecialAssessmentsCustomer[0], 'amount')) : 0)
        : Number(Formatters.getFloat(currentOrderDetails, 'currentSA', getFFBFloatDecimals, 1));

    // const recommendedSA = Number(Formatters.getFloat(currentOrderDetails, 'sa', getFFBFloatDecimals, 1));
    const recommendedSA = (_.size(nonStandardSpecialAssessments) > 0) ?
        ((Number(Formatters.getStr(nonStandardSpecialAssessments[0], 'year')) === currentBeginYear) ?
            Number(Formatters.getStr(nonStandardSpecialAssessments[0], 'amount')) : 0)
        : Number(Formatters.getFloat(currentOrderDetails, 'sa', getFFBFloatDecimals, 1));

    let CurrentMRCTable = buildMRCTable(CurrentMRCVal, cAnnualIncrease, cSecondaryAnnualIncrease, cSecondIncreaseYears, mrcTableYears, 'CurrentMRCTable');
    let RecommendedMRCTable = buildMRCTable(RecommendedMRCVal, rAnnualIncrease, rSecondaryAnnualIncrease, rSecondIncreaseYears, mrcTableYears, 'RecommendedMRCTable');
    let MinimumMRCTable = buildMRCTable(MinimumMRCVal, mAnnualIncrease, mSecondaryAnnualIncrease, mSecondIncreaseYears, mrcTableYears, 'MinimumMRCTable');
    let HypoMRCTable = buildMRCTable(hypoMRCVal, hAnnualIncrease, hSecondaryAnnualIncrease, hSecondIncreaseYears, 30, 'HypoMRCTable');
    let CaDiscMRCTable = buildMRCTable(caDiscMRCVal, caDiscAnnualIncrease, caDiscSecondaryAnnualIncrease, caDiscSecondIncreaseYears, 30, 'CaDiscMRCTable');

    let CurrentMRCIncome = 0;
    let CurrentBalance = beginningBalance;
    let RecommendedBalance = beginningBalance;
    let MinimumBalance = beginningBalance;
    let CaDiscBalance = beginningBalance;

    let isResolved = false;

    const debugCalculationInputVals = {
        years: years,
        mrcValues: {
            CurrentMRCVal: CurrentMRCVal,
            MinimumMRCVal: MinimumMRCVal,
            RecommendedMRCVal: RecommendedMRCVal,
            caDiscMRCVal: caDiscMRCVal,
        },
        details: {
            beginningBalance: beginningBalance,
            currentInterest: currentInterest,
            currentSA: currentSA,
            currentInflation: currentInflation,
            currentPerFirstYear: currentPerFirstYear,
            currentStdMonths: currentStdMonths,
            currentBeginYear: currentBeginYear,

            hypoInterest: hypoInterest,
            hypoSA: hypoSA,
            hypoInflation: hypoInflation,
            hypoMRCVal: hypoMRCVal,
            hypoBegBalance: hypoBegBalance,
            hypoRunningBalance: hypoRunningBalance,
        },
        balances: {
            CurrentBalance: CurrentBalance,
            RecommendedBalance: RecommendedBalance,
            MinimumBalance: MinimumBalance,
            CaDiscBalance: CaDiscBalance,
        },
        increases: {
            cAnnualIncrease: cAnnualIncrease,
            rAnnualIncrease: rAnnualIncrease,
            mAnnualIncrease: mAnnualIncrease,
            caDiscAnnualIncrease: caDiscAnnualIncrease,
            cSecondaryAnnualIncrease: cSecondaryAnnualIncrease,
            rSecondaryAnnualIncrease: rSecondaryAnnualIncrease,
            mSecondaryAnnualIncrease: mSecondaryAnnualIncrease,
            caDiscSecondaryAnnualIncrease: caDiscSecondaryAnnualIncrease,
        },
        other: {
            hAnnualIncrease: hAnnualIncrease,
            hSecondaryAnnualIncrease: hSecondaryAnnualIncrease,
            hYears: hSecondIncreaseYears,
            currentYears: cSecondIncreaseYears,
            recommendedYears: rSecondIncreaseYears,
            minimumYears: mSecondIncreaseYears,
        },
        argCurrentOrderDetails: currentOrderDetails,
        mrcTables: {
            CurrentMRCTable: CurrentMRCTable,
            RecommendedMRCTable: RecommendedMRCTable,
            MinimumMRCTable: MinimumMRCTable,
            CaDiscMRCTable: CaDiscMRCTable,
        }
    };

    const inflationRate = currentInflation; // / 100;
    const hypoInflationRate = hypoInflation; // / 100;

    invMap = buildInvMap(inventory, mrcTableYears, currentBeginYear, inflationRate, hypoInflationRate);
    const specialAssessmentsGrid = buildSpecialAssessments(currentBeginYear, nonStandardSpecialAssessments);
    const specialAssessmentsCustomerGrid = buildSpecialAssessments(currentBeginYear, nonStandardSpecialAssessmentsCustomer);

    while (!isResolved) {
        /// Init Our Hypotheicals
        let hypoPctFunded = 0;

        for (let yearNumIndex: number = 0; yearNumIndex < mrcTableYears; yearNumIndex++) {
            let calendarYearPerIteration = currentBeginYear + yearNumIndex;
            let saGridObject = _.find(specialAssessmentsGrid, ['year', calendarYearPerIteration]);
            let saCGridObject = _.find(specialAssessmentsCustomerGrid, ['year', calendarYearPerIteration]);
            let customerDictatedNonFirstYearSpecialAssessment: number = 0;
            let nonFirstYearSpecialAssessment = _.get(saGridObject, 'amount');
            let nonFirstYearSpecialAssessmentCustomer = _.get(saCGridObject, 'amount');

            if (_.size(nonStandardSpecialAssessmentsCustomer) > 0) {
                customerDictatedNonFirstYearSpecialAssessment = nonFirstYearSpecialAssessmentCustomer;
            } else {
                customerDictatedNonFirstYearSpecialAssessment = nonFirstYearSpecialAssessment;
            }

            const isFirstYear = (yearNumIndex === 0);
            const yearNumOffset = yearNumIndex + 1;

            const annualInterest = computeAnnualInterest(isFirstYear, currentInterest, currentPerFirstYear, currentStdMonths);
            const hypoAnnualInterest = computeAnnualInterest(isFirstYear, hypoInterest, currentPerFirstYear, currentStdMonths);

            /* CURRENT ITEM */ /* CURRENT REFERS TO 'WHAT THE CLIENT IS ALREADY DOING' */
            let currentExpenditures = getExpendituresByYear(invMap, yearNumIndex);
            let hypoExpenditures = getHypoExpendituresByYear(invMap, yearNumIndex);

            const CurrentMRC = CurrentMRCTable[yearNumIndex];

            let currentInterestIncome = 0;
            if (isFirstYear) {
                const hIntInc = annualInterest * ((CurrentMRCVal * currentPerFirstYear / 2) + beginningBalance + currentSA - (currentExpenditures / 2));
                // const hIntInc = annualInterest * ((CurrentMRCVal * currentPerFirstYear / 2) + beginningBalance - (currentExpenditures / 2));
                currentInterestIncome = hIntInc;
            } else {
                if (yearNumOffset <= cSecondIncreaseYears) {
                    const hUdf = computeMrcUdfMRC(CurrentMRCVal, cAnnualIncrease, yearNumIndex);
                    // const hIntInc = annualInterest * ((hUdf * 6) + beginningBalance - (currentExpenditures / 2));
                    const hIntInc = annualInterest * ((hUdf * 6) + CurrentBalance + customerDictatedNonFirstYearSpecialAssessment - (currentExpenditures / 2));
                    // console.log(`current1 - hUdf ${hUdf} CurrentMRCVal ${CurrentMRCVal} cAnnualIncrease ${cAnnualIncrease} `
                    //     + `yearNumIndex ${yearNumIndex} annualInterest ${annualInterest} beginningBalance ${beginningBalance} currentExpenditures ${currentExpenditures}`);
                    currentInterestIncome = hIntInc;
                    // console.log(`current1 - mrc primary currentInterestIncome ${currentInterestIncome} yearNumIndex ${yearNumIndex} yearNumOffset ${yearNumOffset} cSecondIncreaseYears ${cSecondIncreaseYears}`);
                } else {
                    const hUdf = computeMrcUdfMrcSecondary(CurrentMRCVal, cAnnualIncrease, cSecondaryAnnualIncrease, yearNumIndex, cSecondIncreaseYears);
                    // const hIntInc = annualInterest * ((hUdf * 6) + beginningBalance - (currentExpenditures / 2));
                    const hIntInc = annualInterest * ((hUdf * 6) + CurrentBalance + customerDictatedNonFirstYearSpecialAssessment - (currentExpenditures / 2));
                    currentInterestIncome = hIntInc;
                    // console.log(`current2 - mrc secondary currentInterestIncome ${currentInterestIncome} yearNumIndex ${yearNumIndex} yearNumOffset ${yearNumOffset} cSecondIncreaseYears ${cSecondIncreaseYears}`);
                }
            }

            // currentInterestIncome = currentInterestIncome > 0 ? currentInterestIncome : 0; //only changed to match style with other three datasets
            if (currentInterestIncome < 0) { currentInterestIncome = 0; }

            CurrentMRCIncome = (isFirstYear) ?
                // First Year
                // computeMrcUdfMRC(CurrentMRCVal, cAnnualIncrease, yearNumIndex) * currentPerFirstYear + currentSA :
                computeMrcUdfMRC(CurrentMRCVal, cAnnualIncrease, yearNumIndex) * currentPerFirstYear :
                (yearNumOffset <= cSecondIncreaseYears) ?
                    computeMrcUdfMRC(CurrentMRCVal, cAnnualIncrease, yearNumIndex) * 12 :
                    computeMrcUdfMrcSecondary(CurrentMRCVal, cAnnualIncrease, cSecondaryAnnualIncrease, yearNumIndex, cSecondIncreaseYears) * 12;

            CurrentMRCIncome = (isFirstYear) ?
                // First Year
                (CurrentMRC * currentPerFirstYear) + currentSA :
                // (CurrentMRC * currentPerFirstYear) :
                // Subsquent Years
                (CurrentMRC * 12) + customerDictatedNonFirstYearSpecialAssessment;

            let currentFFB = getFFBByYear(invMap, yearNumIndex);
            let endingFFB = getEFFBByYear(invMap, yearNumIndex);
            let hypoFFB = getHypoFFBByYear(invMap, yearNumIndex);
            let hypoEndingFFB = getHypoEFFBByYear(invMap, yearNumIndex);

            let currentStartingBalance = CurrentBalance;

            const cbRes = CurrentBalance + currentInterestIncome + CurrentMRCIncome - currentExpenditures;
            // console.log(`logic::computeFundingCalculations cbRes ${cbRes} = CurrentBalance ${CurrentBalance} + currentInterestIncome ${currentInterestIncome} + CurrentMRCIncome ${CurrentMRCIncome} - currentExpenditures ${currentExpenditures};`);

            CurrentBalance = CurrentBalance + currentInterestIncome + CurrentMRCIncome - currentExpenditures;

            let currentPctFunded = computeItemPercentFunded(currentStartingBalance, currentFFB);

            // RECOMMENDED VALUES
            let recommendedInterestIncome = 0;
            let recommendedMRC = 0;
            let recommendedMRCIncome = 0;
            let recommendedSpecialAssessments = 0;

            recommendedMRC = RecommendedMRCTable[yearNumIndex];

            if (isFirstYear) {
                const hIntInc = annualInterest * ((RecommendedMRCVal * currentPerFirstYear / 2) + beginningBalance + recommendedSA - (currentExpenditures / 2));
                recommendedInterestIncome = hIntInc;
                recommendedSpecialAssessments = recommendedSA;
            } else {
                if (yearNumOffset <= rSecondIncreaseYears) {
                    const hUdf = computeMrcUdfMRC(RecommendedMRCVal, rAnnualIncrease, yearNumIndex);
                    // const hIntInc = annualInterest * ((hUdf * 6) + beginningBalance - (currentExpenditures / 2));
                    const hIntInc = annualInterest * ((hUdf * 6) + RecommendedBalance + nonFirstYearSpecialAssessment - (currentExpenditures / 2));
                    recommendedInterestIncome = hIntInc;
                } else {
                    const hUdf = computeMrcUdfMrcSecondary(RecommendedMRCVal, rAnnualIncrease, rSecondaryAnnualIncrease, yearNumIndex, rSecondIncreaseYears);
                    // const hIntInc = annualInterest * ((hUdf * 6) + beginningBalance - (currentExpenditures / 2));
                    const hIntInc = annualInterest * ((hUdf * 6) + RecommendedBalance + nonFirstYearSpecialAssessment - (currentExpenditures / 2));
                    recommendedInterestIncome = hIntInc;
                }
                recommendedSpecialAssessments = nonFirstYearSpecialAssessment;
            }

            // console.log(`if (recommendedInterestIncome ${recommendedInterestIncome} < 0)`);
            if (recommendedInterestIncome < 0) { recommendedInterestIncome = 0; }

            recommendedMRCIncome = (isFirstYear) ?
                // First Year
                (recommendedMRC * currentPerFirstYear) + recommendedSA :
                // Subsquent Years
                (recommendedMRC * 12) + nonFirstYearSpecialAssessment;

            let recommendedStartingBalance = RecommendedBalance;
            RecommendedBalance = RecommendedBalance + recommendedInterestIncome + recommendedMRCIncome - currentExpenditures;

            let recommendedPctFunded = computeItemPercentFunded(recommendedStartingBalance, currentFFB);

            // HYPOTHETICAL VALUES
            let hypoInterestIncome = 0;
            let hypoMRC = 0;
            let hypoMRCIncome = 0;
            let hypoSpecialAssessments = 0;

            hypoMRC = HypoMRCTable[yearNumIndex];

            if (isFirstYear) {
                const hIntInc = hypoAnnualInterest * ((hypoMRCVal * currentPerFirstYear / 2) + hypoBegBalance + hypoSA - (hypoExpenditures / 2));
                hypoInterestIncome = hIntInc;
                hypoSpecialAssessments = hypoSA;
                // console.log(`logic hypospecialAssessments ${yearNumIndex} hypoSA ${hypoSA} hypoSpecialAssessments ${hypoSpecialAssessments}`, { HypoMRCTable, debugCalculationInputVals });
            } else {
                if (yearNumOffset <= hSecondIncreaseYears) {
                    const hUdf = computeMrcUdfMRC(hypoMRCVal, hAnnualIncrease, yearNumIndex);
                    // 20101004 Working:
                    // const hIntInc = hypoAnnualInterest * ((hUdf * 6) + hypoBegBalance - (hypoExpenditures / 2));
                    const hIntInc = hypoAnnualInterest * ((hUdf * 6) + hypoRunningBalance + hypoSA - (hypoExpenditures / 2));
                    hypoInterestIncome = hIntInc;
                } else {
                    const hUdf = computeMrcUdfMrcSecondary(hypoMRCVal, hAnnualIncrease, hSecondaryAnnualIncrease, yearNumIndex, hSecondIncreaseYears);
                    // PRE 11/1/2019
                    // const hIntInc2 = hypoAnnualInterest * ((hUdf * 6) + hypoBegBalance - (hypoExpenditures / 2));
                    // console.log(`1Secondary Year hIntInc2 ${hIntInc2} = hypoAnnualInterest ${hypoAnnualInterest} * ((hUdf ${hUdf} * 6) + hypoBegBalance ${hypoBegBalance} - (hypoExpenditures ${hypoExpenditures} / 2));`);

                    // POST 11/26/2019 - INCLUDE hYPOSA
                    const hIntInc3 = hypoAnnualInterest * ((hUdf * 6) + hypoRunningBalance + hypoSA - (hypoExpenditures / 2));
                    // console.log(`3Secondary Year hIntInc3 ${hIntInc3} = hypoAnnualInterest ${hypoAnnualInterest} * ((hUdf ${hUdf} * 6)
                    // + hypoRunningBalance ${hypoRunningBalance} - (hypoExpenditures ${hypoExpenditures} / 2));`);
                    hypoInterestIncome = hIntInc3;

                    // PRE 11/26/2019 - WORKING FOR ALL EXCEPT 20258
                    /*
                    const hIntInc = hypoAnnualInterest * ((hUdf * 6) + hypoRunningBalance - (hypoExpenditures / 2));
                    console.log(`2Secondary Year hIntInc ${hIntInc} = hypoAnnualInterest ${hypoAnnualInterest} * ((hUdf ${hUdf} * 6) +
                    // hypoRunningBalance ${hypoRunningBalance} - (hypoExpenditures ${hypoExpenditures} / 2));`);
                    hypoInterestIncome = hIntInc;
                    */
                }
            }

            // console.log(`yearNumIndex ${yearNumIndex} hypoSA ${hypoSA} if (hypoInterestIncome ${hypoInterestIncome} < 0) { hypoInterestIncome = 0; }`, { hypoMRC });
            if (hypoInterestIncome < 0) { hypoInterestIncome = 0; }

            hypoMRCIncome = (isFirstYear) ?
                // First Year
                // WORKING 20190104 computeMrcUdfMRC(hypoMRCVal, hAnnualIncrease, yearNumIndex) * currentPerFirstYear + hypoSA :
                computeMrcUdfMRC(hypoMRCVal, hAnnualIncrease, yearNumIndex) * currentPerFirstYear + hypoSA :
                // computeMrcUdfMRC(hypoMRCVal, hAnnualIncrease, yearNumIndex) * currentPerFirstYear :
                (yearNumOffset <= hSecondIncreaseYears) ?
                    computeMrcUdfMRC(hypoMRCVal, hAnnualIncrease, yearNumIndex) * 12 :
                    computeMrcUdfMrcSecondary(hypoMRCVal, hAnnualIncrease, hSecondaryAnnualIncrease, yearNumIndex, hSecondIncreaseYears) * 12;

            let hypoStartingBalance = 0;
            if (isFirstYear) {
                // 20200128: was hypoStartingBalance = Number(hypoBegBalance);
                hypoStartingBalance = Number(hypoBegBalance);
            } else {
                hypoStartingBalance = Number(hypoEndingBalance);
            }

            let hypoReserveIncome = 0;
            let nextCurHypBal = 0;
            if (isFirstYear) {
                nextCurHypBal = hypoBegBalance + hypoInterestIncome + hypoMRCIncome - hypoExpenditures - hypoSA;
                hypoReserveIncome = hypoMRCIncome - hypoSA;
                hypoEndingBalance = Number(hypoBegBalance + hypoInterestIncome + hypoMRCIncome - hypoExpenditures);
            } else {
                nextCurHypBal = hypoRunningBalance + hypoInterestIncome + hypoMRCIncome - hypoExpenditures;
                hypoReserveIncome = hypoMRCIncome;
                hypoEndingBalance = Number(hypoEndingBalance + hypoInterestIncome + hypoMRCIncome - hypoExpenditures);
            }

            hypoRunningBalance = nextCurHypBal;

            hypoPctFunded = computeItemPercentFunded(hypoStartingBalance, hypoFFB);

            // MINIMUM VALUES
            let minimumInterestIncome = 0;
            let minimumMRC = 0;
            let minimumMRCIncome = 0;
            minimumMRC = MinimumMRCTable[yearNumIndex];

            if (isFirstYear) {
                const hIntInc = annualInterest * ((MinimumMRCVal * currentPerFirstYear / 2) + beginningBalance + recommendedSA - (currentExpenditures / 2));
                minimumInterestIncome = hIntInc;
            } else {
                if (yearNumOffset <= mSecondIncreaseYears) {
                    const hUdf = computeMrcUdfMRC(MinimumMRCVal, mAnnualIncrease, yearNumIndex);
                    const hIntInc = annualInterest * ((hUdf * 6) + MinimumBalance + nonFirstYearSpecialAssessment - (currentExpenditures / 2));
                    minimumInterestIncome = hIntInc;
                } else {
                    const hUdf = computeMrcUdfMrcSecondary(MinimumMRCVal, mAnnualIncrease, mSecondaryAnnualIncrease, yearNumIndex, mSecondIncreaseYears);
                    const hIntInc = annualInterest * ((hUdf * 6) + MinimumBalance + nonFirstYearSpecialAssessment - (currentExpenditures / 2));
                    minimumInterestIncome = hIntInc;
                }
            }

            if (minimumInterestIncome < 0) { minimumInterestIncome = 0; }

            minimumMRCIncome = (isFirstYear) ?
                // First Year
                minimumMRC * currentPerFirstYear + recommendedSA :
                // Subsquent Years
                (minimumMRC * 12) + nonFirstYearSpecialAssessment;
            let minimumStartingBalance = MinimumBalance;
            MinimumBalance = MinimumBalance + minimumInterestIncome + minimumMRCIncome - currentExpenditures;
            let minimumPctFunded = computeItemPercentFunded(minimumStartingBalance, currentFFB);

            // CA-DISCLOSURE VALUES
            let caDiscInterestIncome = 0;
            let caDiscMRC = 0;
            let caDiscMRCIncome = 0;

            caDiscMRC = CaDiscMRCTable[yearNumIndex];

            if (isFirstYear) {
                const hIntInc = annualInterest * ((caDiscMRCVal * currentPerFirstYear / 2) + beginningBalance + caDiscSpecialAssessments - (currentExpenditures / 2));
                caDiscInterestIncome = hIntInc;
                // caDiscSpecialAssessments = currentSA;
            } else {
                if (yearNumOffset <= caDiscSecondIncreaseYears) {
                    const hUdf = computeMrcUdfMRC(caDiscMRCVal, caDiscAnnualIncrease, yearNumIndex);
                    const hIntInc = annualInterest * ((hUdf * 6) + CaDiscBalance - (currentExpenditures / 2));
                    caDiscInterestIncome = hIntInc;
                } else {
                    const hUdf = computeMrcUdfMrcSecondary(caDiscMRCVal, caDiscAnnualIncrease, caDiscSecondaryAnnualIncrease, yearNumIndex, caDiscSecondIncreaseYears);
                    const hIntInc = annualInterest * ((hUdf * 6) + CaDiscBalance - (currentExpenditures / 2));
                    caDiscInterestIncome = hIntInc;
                }
            }

            if (caDiscInterestIncome < 0) { caDiscInterestIncome = 0; }

            caDiscMRCIncome = (isFirstYear) ?
                // First Year
                (caDiscMRC * currentPerFirstYear) + caDiscSpecialAssessments :
                // Subsquent Years
                caDiscMRC * 12;

            let caDiscStartingBalance = CaDiscBalance;
            CaDiscBalance = CaDiscBalance + caDiscInterestIncome + caDiscMRCIncome - currentExpenditures;

            let caDiscPctFunded = computeItemPercentFunded(caDiscStartingBalance, currentFFB);

            // console.log(`currentPctFunded ${currentPctFunded} hypoPctFunded ${hypoPctFunded} minimumPctFunded ${minimumPctFunded} recommendedPctFunded ${recommendedPctFunded}`);

            const resItem = {
                currentBalance: CurrentBalance,
                currentExpenditures: getExpendituresByYear(invMap, yearNumIndex),
                currentFFB: currentFFB,
                endingFFB: endingFFB,
                currentInterestIncome: currentInterestIncome,
                currentMRC: CurrentMRC,
                currentMRCIncome: CurrentMRCIncome,
                currentPctFunded: currentPctFunded,
                currentStartingBalance: currentStartingBalance,
                currentSA: currentSA,
                hypoBalance: hypoBegBalance,
                hypoExpenditures: hypoExpenditures,
                hypoFFB: hypoFFB,
                hypoEndingFFB: hypoEndingFFB,
                hypoInterestIncome: hypoInterestIncome,
                hypoEndingBalance: hypoEndingBalance,
                hypoReserveIncome: hypoReserveIncome,
                hypoMRC: hypoMRC,
                hypoMRCIncome: hypoMRCIncome,
                hypoPctFunded: hypoPctFunded,
                hypoSpecialAssessments: hypoSpecialAssessments,
                hypoStartingBalance: hypoStartingBalance,
                minimumBalance: MinimumBalance,
                minimumInterestIncome: minimumInterestIncome,
                minimumMRC: minimumMRC,
                minimumMRCIncome: minimumMRCIncome,
                minimumPctFunded: minimumPctFunded,
                minimumStartingBalance: minimumStartingBalance,
                recommendedBalance: RecommendedBalance,
                recommendedInterestIncome: recommendedInterestIncome,
                recommendedMRC: recommendedMRC,
                recommendedMRCIncome: recommendedMRCIncome,
                recommendedPctFunded: recommendedPctFunded,
                recommendedSpecialAssessments: recommendedSpecialAssessments,
                recommendedStartingBalance: recommendedStartingBalance,
                caDiscBalance: CaDiscBalance,
                caDiscInterestIncome: caDiscInterestIncome,
                caDiscMRC: caDiscMRC,
                caDiscMRCIncome: caDiscMRCIncome,
                caDiscPctFunded: caDiscPctFunded,
                caDiscSpecialAssessments: caDiscSpecialAssessments,
                caDiscStartingBalance: caDiscStartingBalance,
                year: Number(currentBeginYear + yearNumIndex),
            };

            // currentStartingBalance
            // console.log(`logic::computeFundingCalculations yearNumIndex ${yearNumIndex} year ${resItem.year}`, { resItem });

            recordSet.push(resItem);
        }

        isResolved = true;
    }

    return {
        recordSet: recordSet,
        invMap: invMap,
    };
};

export const calculateInventoryItemAvgCost = (item: any): number => {
    let result: number = 0;

    if (!item) { return result; }

    const bestCost = Number(AppUtilities.castNumber(Formatters.getStr(item, 'bestCost')));
    const worstCost = Number(AppUtilities.castNumber(Formatters.getStr(item, 'worstCost')));

    if (bestCost > 0 || worstCost > 0) {
        result = (bestCost + worstCost) / 2.00;
    }

    return result;
};



// WEBPACK FOOTER //
// ./src/report/logic.tsx